import { Avatar, Box } from '@mui/material';
import {
  LessonComponentResponseType,
  SliderComponent
} from '../../models/LessonComponent';
import React, { useEffect, useState } from 'react';
import { isNil, shuffle } from 'lodash';

import AssetService from '../../../shared/services/AssetService';
import { CustomSlider } from './SliderCustomized';
import Grid from '@mui/material/Unstable_Grid2';
import ImageIcon from '@mui/icons-material/Image';
import LessonComponentService from '../../services/LessonComponentService';
import { SliderItem } from '../../models/LessonComponentContent';
import { styled } from '@mui/system';
import styles from '../../styles/Slider.module.scss';
import { useLessonContext } from '../../models/LessonContext';

interface LearnerViewProps {
  component: SliderComponent;
  items: SliderItem[];
  onChange: (response: LessonComponentResponseType) => void;
}

/** make custom size ImageIcon to use within Avatar */
export const CustomImageIcon = styled(ImageIcon)({
  height: 150,
  width: 150,
  '&.MuiSvgIcon-root': {
    height: 150,
    width: 150
  }
});

export const SliderLearnerView = (props: LearnerViewProps) => {
  const { mode } = useLessonContext();

  const [items, setItems] = useState(props.items);

  const sliderInitialValue = items.length <= 2 ? 0.5 : (items.length - 1) / 2;
  const [sliderValue, setSliderValue] = useState(sliderInitialValue);
  const [sliderState, setSliderState] = useState(false);
  const sliderMaxValue = items.length <= 2 ? 1 : items.length - 1;

  /** set randomized items for learner if randomizeOptions is selected. */
  useEffect(() => {
    if (props.component.settings.randomizeOptions) {
      setItems(getShuffledItems(props.items));
    }
  }, []);

  /** Set the slider value chosen by the user. */
  useEffect(() => {
    if (props.component.answer) {
      const userAnswerIndex = items.findIndex(
        (item) => item.id === props.component.answer
      );
      setSliderValue(userAnswerIndex);
    }
  }, [props.component.answer, items]);

  /** Reset slider to default when user completed the lesson without answer. */
  useEffect(() => {
    if (isNil(props.component.answer)) {
      resetSlider();
    }
  }, [props.component.answer]);

  const resetSlider = () => {
    setSliderValue(sliderInitialValue);
    setSliderState(false);
  };

  const getShuffledItems = (items: SliderItem[]) => {
    let shuffledItems: SliderItem[];
    // Checking to ensure the slider items not in the author's set order.
    for (let iter = 10; iter >= 0; iter -= 1) {
      shuffledItems = shuffle(items);
      if (shuffledItems.some((item, index) => item.id !== items[index].id)) {
        break;
      }
    }
    return shuffledItems;
  };

  /** Find the user selected slider item */
  const selectedItem = items[sliderValue];

  /** Check at least any one of the items has an image */
  const showImage = items.some((item) => item.image);

  /** learner information */
  const LearnerInfo = () => (
    <Box m="auto">Move the slider to make a selection</Box>
  );

  /** Save the slider value as user answer only for learner*/
  const handleSliderChange = (event: Event, newValue: number) => {
    if (mode !== 'edit' && items.length > 1) {
      setSliderValue(newValue);
      setSliderState(true);
      props.onChange(items[newValue].id);
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      {props.component.answer ? (
        <Grid
          className={`${
            !showImage && mode !== 'mark' && styles.learnerInfoText
          }`}
        >
          {selectedItem?.image ? (
            <Avatar
              alt={selectedItem.label}
              src={AssetService.getAssetUrl(selectedItem.image)}
              className={styles.sliderImage}
            />
          ) : (
            showImage && (
              <Avatar className={styles.sliderImage}>
                <CustomImageIcon />
              </Avatar>
            )
          )}
        </Grid>
      ) : (
        mode !== 'mark' && (
          <Grid
            className={showImage ? styles.sliderImage : styles.learnerInfoText}
          >
            {!sliderState && <LearnerInfo />}
          </Grid>
        )
      )}
      <Grid className={styles.sliderIcon}>
        <CustomSlider
          step={1}
          min={0}
          max={sliderMaxValue}
          defaultValue={Math.min(items.length / 2, 0.5)}
          value={sliderValue}
          onChange={handleSliderChange}
        />
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={styles.itemContainer}
      >
        {items.map((item) => (
          <Grid
            key={item.id}
            className={`${styles.sliderLabel} ${
              (sliderState || props.component.answer) &&
              selectedItem?.id === item.id
                ? styles.active
                : ''
            }`}
          >
            <span
              className="formatted-label ql-editor"
              dangerouslySetInnerHTML={{
                __html: LessonComponentService.getUrlWithSas(item.label)
              }}
              style={{ fontSize: '17px', textAlign: 'center' }}
            ></span>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
